import apiClient from "./axiosConfig";

/**
 * In-memory, per-session cache for rarely-changing lookup GETs
 * (subscription plans, statuses, add-on catalog).
 *
 * - Concurrent callers share one in-flight request.
 * - Failed requests are not cached.
 * - Entries expire after `ttlMs` (default 5 min) so long sessions still pick up changes.
 * - Cleared on logout (see useLogout) and by pages that mutate the underlying data.
 *
 * Returns the full axios response, so callers keep reading `response.data...` exactly as before.
 */
const cache = new Map(); // url -> { promise, expiresAt }

export function cachedGet(url, { ttlMs = 5 * 60 * 1000 } = {}) {
  const now = Date.now();
  const hit = cache.get(url);
  if (hit && hit.expiresAt > now) return hit.promise;

  const promise = apiClient.get(url).catch((err) => {
    cache.delete(url);
    throw err;
  });
  cache.set(url, { promise, expiresAt: now + ttlMs });
  return promise;
}

/** Drop cached entries whose URL contains `fragment` (or everything when omitted). */
export function invalidateLookup(fragment) {
  if (!fragment) {
    cache.clear();
    return;
  }
  [...cache.keys()].forEach((key) => {
    if (key.includes(fragment)) cache.delete(key);
  });
}
